import { useState } from 'react';
import ScrollReveal from '@/components/ScrollReveal';
import { toast } from 'sonner';
import { Mail, MapPin, ExternalLink, Phone } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import harbour from '@/assets/harbour.jpg';

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const { t } = useLanguage();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error(t('fillRequiredFields'));
      return;
    }
    toast.success(t('messageSent'));
    setForm({ name: '', email: '', phone: '', message: '' });
  };

  return (
    <div>
      <section className="relative h-[75vh] flex items-end overflow-hidden">
        <img src={harbour} alt="Sivota harbour" className="absolute inset-0 w-full h-full object-cover hero-image-bright" width={1920} height={1080} fetchPriority="high" style={{ animation: 'heroZoom 25s ease-out forwards' }} />
        <div className="hero-gradient absolute inset-0" />
        <div className="relative z-10 section-padding pb-16 lg:pb-24 w-full max-w-3xl">
          <p className="text-white/80 tracking-[0.4em] uppercase text-xs font-sans mb-4 drop-shadow-md">{t('getInTouch')}</p>
          <h1 className="font-serif text-5xl md:text-7xl font-light text-white drop-shadow-lg">{t('contact')}</h1>
        </div>
      </section>

      <section className="section-padding py-24 lg:py-36">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          <ScrollReveal>
            <h2 className="font-serif text-3xl font-light mb-2">{t('sendMessage')}</h2>
            <div className="w-12 h-px bg-golden mb-8" />
            <form onSubmit={handleSubmit} className="space-y-6" noValidate>
              {[
                { key: 'name', label: t('fullName'), type: 'text', autoComplete: 'name' },
                { key: 'email', label: t('email'), type: 'email', autoComplete: 'email' },
                { key: 'phone', label: t('phone'), type: 'tel', autoComplete: 'tel' },
              ].map(f => (
                <div key={f.key}>
                  <label htmlFor={`contact-${f.key}`} className="text-xs text-muted-foreground tracking-wider uppercase mb-2 block">{f.label}</label>
                  <input id={`contact-${f.key}`} type={f.type} autoComplete={f.autoComplete} value={form[f.key as keyof typeof form]}
                    onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                    className="w-full bg-transparent border-b border-border py-3 text-foreground focus:outline-none focus:border-accent transition-colors" maxLength={200} />
                </div>
              ))}
              <div>
                <label htmlFor="contact-message" className="text-xs text-muted-foreground tracking-wider uppercase mb-2 block">{t('message')}</label>
                <textarea id="contact-message" value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
                  rows={5} maxLength={1000} className="w-full bg-transparent border-b border-border py-3 text-foreground focus:outline-none focus:border-accent transition-colors resize-none" />
              </div>
              <button type="submit" className="bg-accent text-accent-foreground px-10 py-4 text-sm tracking-[0.15em] uppercase hover:bg-accent/90 transition-colors mt-2">
                {t('sendMessage')}
              </button>
            </form>
          </ScrollReveal>

          <ScrollReveal delay={0.15}>
            <h2 className="font-serif text-3xl font-light mb-2">{t('contactDetails')}</h2>
            <div className="w-12 h-px bg-golden mb-8" />
            <div className="space-y-8 mb-12">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center shrink-0 mt-0.5" aria-hidden="true"><MapPin size={18} className="text-accent" /></div>
                <div><p className="font-medium text-foreground mb-0.5">{t('location')}</p><p className="text-muted-foreground text-sm">{t('sivotaGreece')}</p></div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center shrink-0 mt-0.5" aria-hidden="true"><Mail size={18} className="text-accent" /></div>
                <div><p className="font-medium text-foreground mb-0.5">{t('email')}</p><a href="mailto:sivotaroyal@gmail.com" className="text-muted-foreground text-sm hover:text-accent transition-colors">sivotaroyal@gmail.com</a></div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center shrink-0 mt-0.5" aria-hidden="true"><Phone size={18} className="text-accent" /></div>
                <div><p className="font-medium text-foreground mb-0.5">{t('phone')}</p><a href="tel:+306948561312" className="text-muted-foreground text-sm hover:text-accent transition-colors">+30 694 856 1312</a></div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center shrink-0 mt-0.5" aria-hidden="true"><ExternalLink size={18} className="text-accent" /></div>
                <div><p className="font-medium text-foreground mb-0.5">{t('booking')}</p>
                  <a href="https://www.booking.com/hotel/gr/sivota-royal.en-gb.html" target="_blank" rel="noopener noreferrer" className="text-accent text-sm hover:underline">{t('viewOnBooking')}</a>
                </div>
              </div>
            </div>
            <div className="w-full h-[300px] rounded-sm overflow-hidden">
              <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3082.9315172226143!2d20.252206576273593!3d39.403055371620646!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x135c81fa0f9ed3a9%3A0xa31f6849d913986b!2sSivota%20Royal!5e0!3m2!1sen!2sus!4v1719951580877!5m2!1sen!2sus"
                width="100%" height="100%" style={{ border: 0, filter: 'saturate(0.8) contrast(1.1)' }} allowFullScreen loading="lazy" title="Sivota Royal Location" />
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
